/**
 * Bootstrap Service
 * Frontend API client for bootstrap & access control endpoints
 */

import { getLicensingApiToken, tryGetSPGraphAccessToken, trySPRestAccessToken } from './authService';
import { getSharePointRootHostname } from './sharePointService';
import type {
    BootstrapStatusResponse,
    RunOnboardingResponse,
    LoginAccessResponse
} from '../../../shared/dto/bootstrap';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Get tenant bootstrap status and user eligibility
 */
export async function getBootstrapStatus(): Promise<BootstrapStatusResponse> {
    const controller = new AbortController();
    // 15s — the backend runs on an Azure Functions plan that scales to zero when
    // idle, so the first request after a break has to wait for a cold start.
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
        const token = await getLicensingApiToken();
        const spGraphToken = await tryGetSPGraphAccessToken();

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
        // Only attach Graph token if we have one (may be absent before SharePoint consent)
        if (spGraphToken) {
            headers['X-SP-Graph-Token'] = spGraphToken;
        }

        const response = await fetch(`${API_BASE}/access/bootstrap-status`, {
            method: 'POST',
            headers,
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(error.error || `Bootstrap status failed: ${response.statusText}`);
        }

        return response.json();
    } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('Bootstrap status check timed out - backend may not be running');
        }
        throw error;
    }
}

/**
 * Run tenant onboarding (idempotent)
 * Optionally sends X-SP-Graph-Token if available (for backend completion check).
 * Never throws if the Graph token is absent — backend handles missing token gracefully.
 */
export async function runOnboarding(): Promise<RunOnboardingResponse> {
    const token = await getLicensingApiToken();
    const spGraphToken = await tryGetSPGraphAccessToken();

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };
    if (spGraphToken) {
        headers['X-SP-Graph-Token'] = spGraphToken;
    }

    const response = await fetch(`${API_BASE}/access/run-onboarding`, {
        method: 'POST',
        headers,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `Onboarding failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Check login access and update LastLoginAt
 */
export async function checkLoginAccess(): Promise<LoginAccessResponse> {
    const controller = new AbortController();
    // 15s — the backend runs on an Azure Functions plan that scales to zero when
    // idle, so the first request after a break has to wait for a cold start.
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
        const token = await getLicensingApiToken();
        const spGraphToken = await tryGetSPGraphAccessToken();

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
        if (spGraphToken) {
            headers['X-SP-Graph-Token'] = spGraphToken;

            // The group-membership check (backend) needs a SharePoint-REST-audience
            // token, whose OAuth resource is host-specific — resolve the tenant's root
            // hostname first (reusing the Graph token above) before requesting it.
            // Best-effort: if either step fails, omit the header and let the backend
            // fail closed rather than blocking the login check itself.
            const hostname = await getSharePointRootHostname(spGraphToken);
            if (hostname) {
                const spRestToken = await trySPRestAccessToken(hostname);
                if (spRestToken) {
                    headers['X-SP-Rest-Token'] = spRestToken;
                }
            }
        }

        const response = await fetch(`${API_BASE}/access/login`, {
            method: 'POST',
            headers,
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        // For access checks, 403 is a valid response (access denied with routing info)
        // Only throw errors for 401, 500, etc.
        if (response.status === 403 || response.ok) {
            return response.json();
        }

        // For other errors (401, 500, etc.), throw
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `Login access check failed: ${response.statusText}`);
    } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('Login access check timed out - backend may not be running');
        }
        throw error;
    }
}
